import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Filter, MessageSquare } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import useScreenSize from '@/modules/common/hooks/device-detection.hook';
import FullPageModal from '@/modules/common/components/ui-kit/full-page-modal/full-page-modal';
import { getChatsAndCallsAgents } from '@/modules/lead-page/api/lead-page.v2.api';
import { LeadPageContext } from '@/modules/lead-page/context/lead-page.context';
import { ActivityItem, WhatsappConversationListItem } from '@/modules/lead-page/dtos/index-v2';
import { useWhatsappConversations } from '@/modules/lead-page/new/hooks/whatsapp-conversations.hook';
import { useZoomCallsActivity } from '@/modules/lead-page/new/hooks/zoom-calls-activity.hook';
import { useAnalyticsContext } from '@/modules/analytics/context/analytics.context';
import { MixpanelTrackingUtil } from '@/modules/analytics/utils';
import { CallSummaryTab } from '@/modules/analytics/types';
import AgentFilterPopover from './agent-filter-popover';
import ChatsAndCallsLeftPanel from './left-panel/chats-and-calls-left-panel';
import WhatsappThreadPanel from './right-panel/whatsapp-thread-panel';
import ZoomCallPanel from './right-panel/zoom-call-panel';

export type SelectedItem =
  | {
      kind: 'whatsapp';
      agentUid: string;
      agentName: string | null;
      conversations: WhatsappConversationListItem[];
    }
  | {
      kind: 'zoom';
      zoomCallUid: string;
      activityItem: ActivityItem;
    };

interface Props {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const EmptyState: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        color: theme.nawy.colors.semantic.text.tertiary,
      }}
    >
      <MessageSquare size={32} />
      <Typography sx={theme.nawy.typography.english.variants.body.md.regular}>
        Select a conversation or call
      </Typography>
    </Box>
  );
};

const ChatsAndCallsTab: React.FC<Props> = ({ isMobileOpen, onMobileClose }) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { lead } = useContext(LeadPageContext);
  const journeyUid = (lead?.data?.lead?.journey_uid as string) ?? '';
  const [selected, setSelected] = useState<SelectedItem | null>(null);
  const { analytics, userInfo } = useAnalyticsContext();

  const handleSelectWhatsapp = (
    agentUid: string,
    agentName: string | null,
    conversations: WhatsappConversationListItem[],
  ) => {
    MixpanelTrackingUtil.trackCallSummaryClicked(
      analytics,
      { summaryType: 'whatsapp', tab: null, agentUid },
      userInfo,
    );
    setSelected({ kind: 'whatsapp', agentUid, agentName, conversations });
  };

  const handleSelectZoom = (zoomCallUid: string, activityItem: ActivityItem) => {
    // The detail body always opens on the Overview tab (see key={zoomCallUid} below).
    MixpanelTrackingUtil.trackCallSummaryClicked(
      analytics,
      { summaryType: 'call', tab: 'overview', zoomCallUid },
      userInfo,
    );
    setSelected({ kind: 'zoom', zoomCallUid, activityItem });
  };

  const handleZoomTabChange = (zoomCallUid: string, tab: CallSummaryTab) => {
    MixpanelTrackingUtil.trackCallSummaryClicked(
      analytics,
      { summaryType: 'call', tab, zoomCallUid },
      userInfo,
    );
  };

  // Agent filter: options come from the journey agents endpoint (whatsapp + zoom,
  // hierarchy-restricted server-side); the selection filters both lists server-side.
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [mobileFilterAnchor, setMobileFilterAnchor] = useState<HTMLElement | null>(null);
  const agentUids = useMemo(() => Array.from(selectedAgents), [selectedAgents]);
  const toggleAgent = (agentUid: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentUid)) {
        next.delete(agentUid);
      } else {
        next.add(agentUid);
      }
      return next;
    });
  };

  const enabled = !!journeyUid && (!isMobile || !!isMobileOpen);

  const { data: agents = [] } = useQuery({
    queryKey: ['chats-and-calls-agents', journeyUid],
    queryFn: () => getChatsAndCallsAgents(journeyUid),
    enabled,
  });

  const {
    totalAgents,
    agentGroups,
    isSuccess: whatsappSuccess,
    isError: whatsappError,
  } = useWhatsappConversations(journeyUid, { enabled, agentUids });

  const {
    zoomItems,
    isSuccess: zoomSuccess,
    isError: zoomError,
  } = useZoomCallsActivity(journeyUid, { enabled, agentUids });

  /* Section visibility is decided once per journey, from the initial
     (unfiltered) call: an empty list or a failed one (e.g. 401 when the
     viewer has no access to that integration) hides the section. Later
     filtered refetches never flip the decision — the Whatsapp header hosts
     the agent filter, so hiding on a filtered empty result would strand it.
     Lives here, not in the left panel: the panel unmounts on mobile while a
     conversation is open. */
  const [showWhatsappSection, setShowWhatsappSection] = useState<boolean | null>(null);
  const [showCallsSection, setShowCallsSection] = useState<boolean | null>(null);

  useEffect(() => {
    setShowWhatsappSection(null);
    setShowCallsSection(null);
  }, [journeyUid]);

  useEffect(() => {
    if (showWhatsappSection !== null) return;
    if (whatsappError) setShowWhatsappSection(false);
    else if (whatsappSuccess) setShowWhatsappSection(agentGroups.length > 0);
  }, [showWhatsappSection, whatsappError, whatsappSuccess, agentGroups.length]);

  useEffect(() => {
    if (showCallsSection !== null) return;
    if (zoomError) setShowCallsSection(false);
    else if (zoomSuccess) setShowCallsSection(zoomItems.length > 0);
  }, [showCallsSection, zoomError, zoomSuccess, zoomItems.length]);

  const totalCount = useMemo(
    // Whatsapp is grouped by agent on the backend — `totalAgents` is the group count
    // across all pages; zoom counts the loaded items.
    () => totalAgents + zoomItems.length,
    [totalAgents, zoomItems],
  );

  const listContent = (
    <ChatsAndCallsLeftPanel
      journeyUid={journeyUid}
      selected={selected}
      onSelectWhatsapp={handleSelectWhatsapp}
      onSelectZoom={handleSelectZoom}
      fullWidth={isMobile}
      agents={agents}
      selectedAgents={selectedAgents}
      onToggleAgent={toggleAgent}
      showWhatsappSection={showWhatsappSection}
      showCallsSection={showCallsSection}
    />
  );

  const detailContent = (() => {
    if (selected?.kind === 'whatsapp') {
      return (
        <WhatsappThreadPanel
          journeyUid={journeyUid}
          agentUid={selected.agentUid}
          agentName={selected.agentName}
          conversations={selected.conversations}
          hideHeader={isMobile}
        />
      );
    }
    if (selected?.kind === 'zoom') {
      return (
        <ZoomCallPanel
          // Remount per call so the detail body resets to the Overview tab
          key={selected.zoomCallUid}
          zoomCallUid={selected.zoomCallUid}
          activityItem={selected.activityItem}
          onTabChange={(tab) => handleZoomTabChange(selected.zoomCallUid, tab)}
        />
      );
    }
    return null;
  })();

  if (isMobile) {
    const handleBack = () => {
      if (selected) {
        setSelected(null);
      } else {
        onMobileClose?.();
      }
    };

    let title: React.ReactNode = `Chats & calls${totalCount > 0 ? ` (${totalCount})` : ''}`;
    let extraButtons: React.ReactNode = null;

    if (selected?.kind === 'whatsapp') {
      title = (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              ...theme.nawy.typography.english.variants.headline.xs,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            {selected.agentName ?? 'Agent'}
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            WhatsApp
          </Typography>
        </Box>
      );
    } else if (selected?.kind === 'zoom') {
      const dateText = (() => {
        try {
          return format(parseISO(selected.activityItem.createdAt), 'd MMM yyyy, h:mm a');
        } catch {
          return selected.activityItem.createdAt;
        }
      })();
      title = (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.headline.xs,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Zoom call
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            {dateText}
          </Typography>
        </Box>
      );
    } else {
      extraButtons = (
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="Filter"
            onClick={(e) => setMobileFilterAnchor(e.currentTarget as HTMLElement)}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            }}
          >
            <Filter size={16} />
          </IconButton>
          {selectedAgents.size > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.nawy.colors.semantic.indicator.blue,
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      );
    }

    return (
      <FullPageModal
        isOpen={!!isMobileOpen}
        onClose={handleBack}
        title={title}
        extraButtons={extraButtons}
      >
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
          {selected ? detailContent : listContent}
        </Box>
        <AgentFilterPopover
          anchorEl={mobileFilterAnchor}
          onClose={() => setMobileFilterAnchor(null)}
          agents={agents}
          selectedAgents={selectedAgents}
          onToggleAgent={toggleAgent}
        />
      </FullPageModal>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden' }}>
      {listContent}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {!selected && <EmptyState />}
        {detailContent}
      </Box>
    </Box>
  );
};

export default ChatsAndCallsTab;
